import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";

export function SettingsDialog({ open, onOpenChange }) {
  const { settings, save } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(settings);
  }, [open, settings]);

  const handleChange = (key) => (e) => {
    const v = parseFloat(e.target.value);
    setForm((f) => ({ ...f, [key]: Number.isNaN(v) ? 0 : v }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await save(form);
      toast.success("Settings saved");
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Global Settings</DialogTitle>
          <DialogDescription>
            Defaults applied to new orders. Each order can still override these values.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-500">
              Grain-to-Grass Ratio
            </Label>
            <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3 mt-2">
              <div>
                <Label className="text-xs text-stone-600">Grain (kg)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="settings-grain-kg"
                  value={form.grain_to_grass_grain_kg}
                  onChange={handleChange("grain_to_grass_grain_kg")}
                />
              </div>
              <div className="pb-2.5 text-stone-400 text-sm">→</div>
              <div>
                <Label className="text-xs text-stone-600">Grass (kg)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="settings-grass-kg"
                  value={form.grain_to_grass_grass_kg}
                  onChange={handleChange("grain_to_grass_grass_kg")}
                />
              </div>
            </div>
            <p className="text-xs text-stone-500 mt-2">
              {form.grain_to_grass_grain_kg} kg grain yields {form.grain_to_grass_grass_kg} kg grass.
            </p>
          </div>

          <Separator />

          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-500">
              Target Weight Per Tray
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              className="mt-2"
              data-testid="settings-tray-weight"
              value={form.target_weight_per_tray}
              onChange={handleChange("target_weight_per_tray")}
            />
            <p className="text-xs text-stone-500 mt-2">
              Final harvest weight (kg) from a single tray.
            </p>
          </div>

          <Separator />

          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-500">
              Standard Cycle (Days)
            </Label>
            <Input
              type="number"
              step="0.1"
              min="1"
              className="mt-2"
              data-testid="settings-cycle-days"
              value={form.standard_cycle_days}
              onChange={handleChange("standard_cycle_days")}
            />
            <p className="text-xs text-stone-500 mt-2">
              Default = 7 days / 168 hours.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="settings-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="settings-save-btn"
            className="bg-stone-900 hover:bg-stone-800 text-white"
          >
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
